/**
 * Config-driven data-capture engine for CEO's GPT employee workspaces.
 * Each role maps to a list of form definitions. Each form becomes a
 * `department_submission` of that `type`, with `fields` mapped into `data`.
 *
 * field.type: text | textarea | email | tel | number | currency | date | select
 * field.half: render at half width on desktop
 */

const PRIORITY = ["low", "medium", "high", "urgent"];

export const FORMS = {
    sales: [
        {
            key: "lead", label: "Add Lead", icon: "target",
            desc: "Capture a new sales lead and pipeline entry.",
            fields: [
                { name: "lead_name", label: "Lead Name", type: "text", required: true, half: true },
                { name: "company", label: "Company", type: "text", half: true },
                { name: "industry", label: "Industry", type: "text", half: true },
                { name: "contact_person", label: "Contact Person", type: "text", half: true },
                { name: "phone", label: "Phone", type: "tel", half: true },
                { name: "email", label: "Email", type: "email", half: true },
                { name: "lead_source", label: "Lead Source", type: "select", options: ["Referral", "Website", "Cold Outreach", "Event", "Ad", "Partner"], half: true },
                { name: "expected_value", label: "Expected Deal Value", type: "currency", half: true },
                { name: "stage", label: "Pipeline Stage", type: "select", options: ["New", "Qualified", "Proposal", "Negotiation", "Won", "Lost"], half: true },
                { name: "probability", label: "Probability (%)", type: "number", half: true },
                { name: "owner", label: "Owner", type: "text", half: true },
                { name: "next_followup", label: "Next Follow-up", type: "date", half: true },
                { name: "lead_notes", label: "Notes", type: "textarea" },
            ],
        },
        {
            key: "deal_update", label: "Update Deal", icon: "handshake",
            desc: "Log deal progress, wins and losses.",
            fields: [
                { name: "deal_name", label: "Deal Name", type: "text", required: true, half: true },
                { name: "deal_status", label: "Deal Status", type: "select", options: ["Open", "Negotiation", "Won", "Lost"], half: true },
                { name: "closing_date", label: "Closing Date", type: "date", half: true },
                { name: "competitor", label: "Competitor", type: "text", half: true },
                { name: "reason_won", label: "Reason Won", type: "textarea" },
                { name: "reason_lost", label: "Reason Lost", type: "textarea" },
            ],
        },
        {
            key: "daily_report", label: "Daily Sales Report", icon: "chart",
            desc: "Submit your end-of-day sales summary.",
            fields: [
                { name: "calls_made", label: "Calls Made", type: "number", half: true },
                { name: "meetings", label: "Meetings Conducted", type: "number", half: true },
                { name: "new_leads", label: "New Leads", type: "number", half: true },
                { name: "deals_closed", label: "Deals Closed", type: "number", half: true },
                { name: "revenue_closed", label: "Revenue Closed", type: "currency", half: true },
                { name: "challenges", label: "Challenges", type: "textarea" },
                { name: "opportunities", label: "Opportunities", type: "textarea" },
            ],
        },
    ],
    finance: [
        {
            key: "expense", label: "Add Expense", icon: "receipt",
            desc: "Record a company expense with invoice.",
            fields: [
                { name: "category", label: "Expense Category", type: "select", options: ["Salaries", "Rent", "Marketing", "Software", "Travel", "Utilities", "Other"], half: true },
                { name: "amount", label: "Amount", type: "currency", required: true, half: true },
                { name: "vendor", label: "Vendor", type: "text", half: true },
                { name: "invoice_number", label: "Invoice Number", type: "text", half: true },
                { name: "date", label: "Date", type: "date", half: true },
                { name: "payment_method", label: "Payment Method", type: "select", options: ["Bank Transfer", "Card", "Cash", "UPI", "Cheque"], half: true },
            ],
        },
        {
            key: "revenue", label: "Add Revenue", icon: "trending",
            desc: "Log an incoming revenue entry.",
            fields: [
                { name: "source", label: "Revenue Source", type: "text", required: true, half: true },
                { name: "amount", label: "Amount", type: "currency", required: true, half: true },
                { name: "date", label: "Date", type: "date", half: true },
                { name: "invoice_number", label: "Invoice Number", type: "text", half: true },
                { name: "rev_notes", label: "Notes", type: "textarea" },
            ],
        },
        {
            key: "vendor_payment", label: "Vendor Payment", icon: "wallet",
            desc: "Record a payment made to a vendor.",
            fields: [
                { name: "vendor", label: "Vendor", type: "text", required: true, half: true },
                { name: "amount", label: "Amount", type: "currency", required: true, half: true },
                { name: "payment_method", label: "Payment Method", type: "select", options: ["Bank Transfer", "Card", "Cash", "UPI", "Cheque"], half: true },
                { name: "date", label: "Date", type: "date", half: true },
                { name: "tax_notes", label: "Tax Notes", type: "textarea" },
            ],
        },
        {
            key: "finance_summary", label: "Daily Finance Summary", icon: "chart",
            desc: "End-of-day cash & receivables snapshot.",
            fields: [
                { name: "cash_balance", label: "Cash Balance", type: "currency", half: true },
                { name: "revenue_today", label: "Revenue Today", type: "currency", half: true },
                { name: "expenses_today", label: "Expenses Today", type: "currency", half: true },
                { name: "pending_receivables", label: "Pending Receivables", type: "currency", half: true },
                { name: "pending_payments", label: "Pending Payments", type: "currency", half: true },
                { name: "summary_notes", label: "Notes", type: "textarea" },
            ],
        },
    ],
    hr: [
        {
            key: "candidate", label: "Candidate Entry", icon: "user",
            desc: "Add a hiring candidate to the pipeline.",
            fields: [
                { name: "candidate_name", label: "Candidate Name", type: "text", required: true, half: true },
                { name: "position", label: "Position", type: "text", half: true },
                { name: "email", label: "Email", type: "email", half: true },
                { name: "phone", label: "Phone", type: "tel", half: true },
                { name: "stage", label: "Stage", type: "select", options: ["Applied", "Screening", "Interview", "Offer", "Hired", "Rejected"], half: true },
                { name: "cand_notes", label: "Notes", type: "textarea" },
            ],
        },
        {
            key: "attendance", label: "Attendance", icon: "calendar",
            desc: "Submit team attendance for a day.",
            fields: [
                { name: "date", label: "Date", type: "date", required: true, half: true },
                { name: "present", label: "Present", type: "number", half: true },
                { name: "absent", label: "Absent", type: "number", half: true },
                { name: "on_leave", label: "On Leave", type: "number", half: true },
                { name: "att_notes", label: "Notes", type: "textarea" },
            ],
        },
        {
            key: "leave_request", label: "Leave Request", icon: "calendar",
            desc: "Log an employee leave request.",
            fields: [
                { name: "employee_name", label: "Employee Name", type: "text", required: true, half: true },
                { name: "leave_type", label: "Leave Type", type: "select", options: ["Casual", "Sick", "Earned", "Unpaid", "Maternity", "Paternity"], half: true },
                { name: "from_date", label: "From", type: "date", half: true },
                { name: "to_date", label: "To", type: "date", half: true },
                { name: "reason", label: "Reason", type: "textarea" },
            ],
        },
        {
            key: "performance_review", label: "Performance Review", icon: "star",
            desc: "Record a performance review.",
            fields: [
                { name: "employee_name", label: "Employee Name", type: "text", required: true, half: true },
                { name: "period", label: "Period", type: "text", half: true },
                { name: "rating", label: "Rating", type: "select", options: ["Outstanding", "Exceeds", "Meets", "Below", "Poor"], half: true },
                { name: "strengths", label: "Strengths", type: "textarea" },
                { name: "improvements", label: "Improvements", type: "textarea" },
            ],
        },
        {
            key: "hiring_request", label: "Hiring Request", icon: "user",
            desc: "Raise a request to open a new position.",
            fields: [
                { name: "position", label: "Position", type: "text", required: true, half: true },
                { name: "department", label: "Department", type: "text", half: true },
                { name: "priority", label: "Priority", type: "select", options: PRIORITY, half: true },
                { name: "headcount", label: "Headcount", type: "number", half: true },
                { name: "justification", label: "Justification", type: "textarea" },
            ],
        },
        {
            key: "training_record", label: "Training Record", icon: "star",
            desc: "Log an employee training session.",
            fields: [
                { name: "employee_name", label: "Employee Name", type: "text", required: true, half: true },
                { name: "training", label: "Training", type: "text", half: true },
                { name: "date", label: "Date", type: "date", half: true },
                { name: "train_notes", label: "Notes", type: "textarea" },
            ],
        },
    ],
    operations: [
        {
            key: "daily_task", label: "Daily Task", icon: "check",
            desc: "Log an operational task.",
            fields: [
                { name: "task_title", label: "Task Title", type: "text", required: true, half: true },
                { name: "owner", label: "Owner", type: "text", half: true },
                { name: "status", label: "Status", type: "select", options: ["Not Started", "In Progress", "Blocked", "Done"], half: true },
                { name: "due_date", label: "Due Date", type: "date", half: true },
                { name: "task_notes", label: "Notes", type: "textarea" },
            ],
        },
        {
            key: "inventory_update", label: "Inventory Update", icon: "box",
            desc: "Update stock levels for an item.",
            fields: [
                { name: "item", label: "Item", type: "text", required: true, half: true },
                { name: "sku", label: "SKU", type: "text", half: true },
                { name: "quantity", label: "Quantity", type: "number", half: true },
                { name: "location", label: "Location", type: "text", half: true },
                { name: "inv_notes", label: "Notes", type: "textarea" },
            ],
        },
        {
            key: "warehouse_update", label: "Warehouse Update", icon: "box",
            desc: "Report warehouse status.",
            fields: [
                { name: "warehouse", label: "Warehouse", type: "text", required: true, half: true },
                { name: "status", label: "Status", type: "select", options: ["Operational", "Partial", "Down"], half: true },
                { name: "capacity_used", label: "Capacity Used (%)", type: "number", half: true },
                { name: "wh_notes", label: "Notes", type: "textarea" },
            ],
        },
        {
            key: "project_delay", label: "Project Delay", icon: "alert",
            desc: "Report a delay in delivery.",
            fields: [
                { name: "project", label: "Project", type: "text", required: true, half: true },
                { name: "delay_days", label: "Delay (days)", type: "number", half: true },
                { name: "reason", label: "Reason", type: "textarea" },
                { name: "mitigation", label: "Mitigation", type: "textarea" },
            ],
        },
        {
            key: "operational_risk", label: "Operational Risk", icon: "alert",
            desc: "Log an operational risk.",
            fields: [
                { name: "risk", label: "Risk", type: "text", required: true, half: true },
                { name: "severity", label: "Severity", type: "select", options: ["Low", "Medium", "High", "Critical"], half: true },
                { name: "impact", label: "Impact", type: "textarea" },
                { name: "mitigation", label: "Mitigation", type: "textarea" },
            ],
        },
        {
            key: "vendor_coordination", label: "Vendor Coordination", icon: "handshake",
            desc: "Log coordination with a vendor.",
            fields: [
                { name: "vendor", label: "Vendor", type: "text", required: true, half: true },
                { name: "purpose", label: "Purpose", type: "text", half: true },
                { name: "status", label: "Status", type: "select", options: ["Pending", "In Progress", "Resolved"], half: true },
                { name: "vc_notes", label: "Notes", type: "textarea" },
            ],
        },
    ],
    marketing: [
        {
            key: "campaign", label: "Add Campaign", icon: "megaphone",
            desc: "Create a new marketing campaign.",
            fields: [
                { name: "campaign_name", label: "Campaign Name", type: "text", required: true, half: true },
                { name: "channel", label: "Channel", type: "select", options: ["Google", "Meta", "LinkedIn", "Email", "SEO", "Events", "Influencer"], half: true },
                { name: "budget", label: "Budget", type: "currency", half: true },
                { name: "start_date", label: "Start Date", type: "date", half: true },
                { name: "end_date", label: "End Date", type: "date", half: true },
                { name: "objective", label: "Objective", type: "textarea" },
            ],
        },
        {
            key: "budget_allocation", label: "Budget Allocation", icon: "wallet",
            desc: "Allocate marketing budget to a channel.",
            fields: [
                { name: "channel", label: "Channel", type: "select", options: ["Google", "Meta", "LinkedIn", "Email", "SEO", "Events", "Influencer"], half: true },
                { name: "amount", label: "Amount", type: "currency", required: true, half: true },
                { name: "period", label: "Period", type: "text", half: true },
                { name: "ba_notes", label: "Notes", type: "textarea" },
            ],
        },
        {
            key: "content_calendar", label: "Content Calendar", icon: "calendar",
            desc: "Schedule a content piece.",
            fields: [
                { name: "title", label: "Title", type: "text", required: true, half: true },
                { name: "content_type", label: "Type", type: "select", options: ["Blog", "Social", "Video", "Email", "Ad", "PR"], half: true },
                { name: "publish_date", label: "Publish Date", type: "date", half: true },
                { name: "owner", label: "Owner", type: "text", half: true },
                { name: "status", label: "Status", type: "select", options: ["Idea", "Drafting", "Review", "Scheduled", "Published"], half: true },
            ],
        },
        {
            key: "lead_generation", label: "Lead Generation", icon: "target",
            desc: "Log leads generated from a source.",
            fields: [
                { name: "source", label: "Source", type: "text", required: true, half: true },
                { name: "leads", label: "Leads", type: "number", half: true },
                { name: "cost", label: "Cost", type: "currency", half: true },
                { name: "lg_notes", label: "Notes", type: "textarea" },
            ],
        },
        {
            key: "influencer_activity", label: "Influencer Activity", icon: "star",
            desc: "Track an influencer collaboration.",
            fields: [
                { name: "influencer", label: "Influencer", type: "text", required: true, half: true },
                { name: "platform", label: "Platform", type: "select", options: ["Instagram", "YouTube", "LinkedIn", "X", "TikTok"], half: true },
                { name: "cost", label: "Cost", type: "currency", half: true },
                { name: "reach", label: "Reach", type: "number", half: true },
                { name: "ia_notes", label: "Notes", type: "textarea" },
            ],
        },
        {
            key: "marketing_notes", label: "Marketing Notes", icon: "note",
            desc: "Capture a marketing observation.",
            fields: [
                { name: "note", label: "Note", type: "textarea", required: true },
            ],
        },
    ],
    customer: [
        {
            key: "ticket", label: "Add Ticket", icon: "ticket",
            desc: "Log a customer support ticket.",
            fields: [
                { name: "customer_name", label: "Customer Name", type: "text", required: true, half: true },
                { name: "subject", label: "Subject", type: "text", half: true },
                { name: "channel", label: "Channel", type: "select", options: ["Email", "Phone", "Chat", "Social", "In-app"], half: true },
                { name: "priority", label: "Priority", type: "select", options: PRIORITY, half: true },
                { name: "status", label: "Status", type: "select", options: ["Open", "In Progress", "Resolved", "Closed"], half: true },
                { name: "description", label: "Description", type: "textarea" },
            ],
        },
        {
            key: "complaint", label: "Complaint", icon: "alert",
            desc: "Record a customer complaint.",
            fields: [
                { name: "customer_name", label: "Customer Name", type: "text", required: true, half: true },
                { name: "category", label: "Category", type: "select", options: ["Product", "Service", "Billing", "Delivery", "Other"], half: true },
                { name: "severity", label: "Severity", type: "select", options: ["Low", "Medium", "High"], half: true },
                { name: "description", label: "Description", type: "textarea" },
            ],
        },
        {
            key: "feedback", label: "Feedback", icon: "star",
            desc: "Capture customer feedback.",
            fields: [
                { name: "customer_name", label: "Customer Name", type: "text", required: true, half: true },
                { name: "rating", label: "Rating", type: "select", options: ["5 - Excellent", "4 - Good", "3 - Average", "2 - Poor", "1 - Very Poor"], half: true },
                { name: "comments", label: "Comments", type: "textarea" },
            ],
        },
        {
            key: "refund", label: "Refund Request", icon: "wallet",
            desc: "Log a customer refund request.",
            fields: [
                { name: "customer_name", label: "Customer Name", type: "text", required: true, half: true },
                { name: "order_ref", label: "Order / Invoice", type: "text", half: true },
                { name: "amount", label: "Amount", type: "currency", half: true },
                { name: "status", label: "Status", type: "select", options: ["Requested", "Approved", "Processed", "Declined"], half: true },
                { name: "reason", label: "Reason", type: "textarea" },
            ],
        },
        {
            key: "escalation", label: "Escalation", icon: "alert",
            desc: "Escalate an unresolved issue.",
            fields: [
                { name: "ticket_ref", label: "Ticket Ref", type: "text", required: true, half: true },
                { name: "assigned_to", label: "Assigned To", type: "text", half: true },
                { name: "priority", label: "Priority", type: "select", options: PRIORITY, half: true },
                { name: "reason", label: "Reason", type: "textarea" },
            ],
        },
        {
            key: "support_notes", label: "Support Notes", icon: "note",
            desc: "Capture a support note.",
            fields: [{ name: "note", label: "Note", type: "textarea", required: true }],
        },
    ],
    procurement: [
        {
            key: "purchase_request", label: "Purchase Request", icon: "cart",
            desc: "Raise a request to purchase.",
            fields: [
                { name: "item", label: "Item", type: "text", required: true, half: true },
                { name: "quantity", label: "Quantity", type: "number", half: true },
                { name: "estimated_cost", label: "Estimated Cost", type: "currency", half: true },
                { name: "needed_by", label: "Needed By", type: "date", half: true },
                { name: "justification", label: "Justification", type: "textarea" },
            ],
        },
        {
            key: "vendor", label: "Add Vendor", icon: "handshake",
            desc: "Register a new vendor.",
            fields: [
                { name: "vendor_name", label: "Vendor Name", type: "text", required: true, half: true },
                { name: "contact_person", label: "Contact Person", type: "text", half: true },
                { name: "phone", label: "Phone", type: "tel", half: true },
                { name: "email", label: "Email", type: "email", half: true },
                { name: "category", label: "Category", type: "text", half: true },
                { name: "vendor_notes", label: "Notes", type: "textarea" },
            ],
        },
        {
            key: "quotation", label: "Vendor Quotation", icon: "receipt",
            desc: "Log a vendor quotation.",
            fields: [
                { name: "vendor", label: "Vendor", type: "text", required: true, half: true },
                { name: "item", label: "Item", type: "text", half: true },
                { name: "amount", label: "Amount", type: "currency", half: true },
                { name: "valid_until", label: "Valid Until", type: "date", half: true },
                { name: "quote_notes", label: "Notes", type: "textarea" },
            ],
        },
        {
            key: "delivery_update", label: "Delivery Update", icon: "box",
            desc: "Update a purchase order delivery.",
            fields: [
                { name: "po_number", label: "PO Number", type: "text", required: true, half: true },
                { name: "vendor", label: "Vendor", type: "text", half: true },
                { name: "status", label: "Status", type: "select", options: ["Pending", "Shipped", "Delayed", "Delivered"], half: true },
                { name: "expected_date", label: "Expected Date", type: "date", half: true },
                { name: "del_notes", label: "Notes", type: "textarea" },
            ],
        },
        {
            key: "vendor_evaluation", label: "Vendor Evaluation", icon: "star",
            desc: "Evaluate a vendor's performance.",
            fields: [
                { name: "vendor", label: "Vendor", type: "text", required: true, half: true },
                { name: "quality", label: "Quality", type: "select", options: ["Excellent", "Good", "Average", "Poor"], half: true },
                { name: "timeliness", label: "Timeliness", type: "select", options: ["Excellent", "Good", "Average", "Poor"], half: true },
                { name: "pricing", label: "Pricing", type: "select", options: ["Excellent", "Good", "Average", "Poor"], half: true },
                { name: "comments", label: "Comments", type: "textarea" },
            ],
        },
    ],
    legal: [
        {
            key: "contract", label: "Upload Contract", icon: "file",
            desc: "Record a contract and attach the document.",
            fields: [
                { name: "contract_title", label: "Contract Title", type: "text", required: true, half: true },
                { name: "counterparty", label: "Counterparty", type: "text", half: true },
                { name: "contract_type", label: "Type", type: "select", options: ["NDA", "MSA", "Employment", "Vendor", "Lease", "Other"], half: true },
                { name: "effective_date", label: "Effective Date", type: "date", half: true },
                { name: "expiry_date", label: "Expiry Date", type: "date", half: true },
                { name: "contract_notes", label: "Notes", type: "textarea" },
            ],
        },
        {
            key: "compliance_note", label: "Compliance Note", icon: "shield",
            desc: "Log a compliance observation.",
            fields: [
                { name: "area", label: "Area", type: "text", required: true, half: true },
                { name: "status", label: "Status", type: "select", options: ["Compliant", "At Risk", "Non-Compliant"], half: true },
                { name: "details", label: "Details", type: "textarea" },
            ],
        },
        {
            key: "risk_report", label: "Risk Report", icon: "alert",
            desc: "Report a legal risk.",
            fields: [
                { name: "risk", label: "Risk", type: "text", required: true, half: true },
                { name: "severity", label: "Severity", type: "select", options: ["Low", "Medium", "High", "Critical"], half: true },
                { name: "likelihood", label: "Likelihood", type: "select", options: ["Rare", "Possible", "Likely", "Almost Certain"], half: true },
                { name: "mitigation", label: "Mitigation", type: "textarea" },
            ],
        },
        {
            key: "renewal", label: "Agreement Renewal", icon: "refresh",
            desc: "Track an agreement renewal.",
            fields: [
                { name: "agreement", label: "Agreement", type: "text", required: true, half: true },
                { name: "renewal_date", label: "Renewal Date", type: "date", half: true },
                { name: "owner", label: "Owner", type: "text", half: true },
                { name: "renewal_notes", label: "Notes", type: "textarea" },
            ],
        },
        {
            key: "policy_update", label: "Policy Update", icon: "file",
            desc: "Publish or update a policy.",
            fields: [
                { name: "policy_name", label: "Policy Name", type: "text", required: true, half: true },
                { name: "version", label: "Version", type: "text", half: true },
                { name: "effective_date", label: "Effective Date", type: "date", half: true },
                { name: "summary", label: "Summary", type: "textarea" },
            ],
        },
    ],
    strategy: [
        {
            key: "growth_initiative", label: "Growth Initiative", icon: "trending",
            desc: "Propose a growth initiative.",
            fields: [
                { name: "title", label: "Title", type: "text", required: true, half: true },
                { name: "owner", label: "Owner", type: "text", half: true },
                { name: "timeline", label: "Timeline", type: "text", half: true },
                { name: "objective", label: "Objective", type: "textarea" },
                { name: "expected_impact", label: "Expected Impact", type: "textarea" },
            ],
        },
        {
            key: "business_goal", label: "Business Goal", icon: "target",
            desc: "Set a measurable business goal.",
            fields: [
                { name: "goal", label: "Goal", type: "text", required: true, half: true },
                { name: "metric", label: "Metric", type: "text", half: true },
                { name: "target", label: "Target", type: "text", half: true },
                { name: "deadline", label: "Deadline", type: "date", half: true },
                { name: "goal_notes", label: "Notes", type: "textarea" },
            ],
        },
        {
            key: "expansion_proposal", label: "Expansion Proposal", icon: "globe",
            desc: "Propose a market expansion.",
            fields: [
                { name: "market", label: "Market / Region", type: "text", required: true, half: true },
                { name: "investment", label: "Investment", type: "currency", half: true },
                { name: "timeline", label: "Timeline", type: "text", half: true },
                { name: "rationale", label: "Rationale", type: "textarea" },
            ],
        },
        {
            key: "competitor_analysis", label: "Competitor Analysis", icon: "search",
            desc: "Analyse a competitor.",
            fields: [
                { name: "competitor", label: "Competitor", type: "text", required: true, half: true },
                { name: "threat_level", label: "Threat Level", type: "select", options: ["Low", "Medium", "High"], half: true },
                { name: "strengths", label: "Strengths", type: "textarea" },
                { name: "weaknesses", label: "Weaknesses", type: "textarea" },
            ],
        },
        {
            key: "market_research", label: "Market Research", icon: "search",
            desc: "Log market research findings.",
            fields: [
                { name: "topic", label: "Topic", type: "text", required: true, half: true },
                { name: "source", label: "Source", type: "text", half: true },
                { name: "date", label: "Date", type: "date", half: true },
                { name: "findings", label: "Findings", type: "textarea" },
            ],
        },
        {
            key: "partnership_proposal", label: "Partnership Proposal", icon: "handshake",
            desc: "Propose a strategic partnership.",
            fields: [
                { name: "partner", label: "Partner", type: "text", required: true, half: true },
                { name: "type", label: "Type", type: "select", options: ["Reseller", "Technology", "Distribution", "Co-marketing", "Other"], half: true },
                { name: "value", label: "Value", type: "currency", half: true },
                { name: "pp_notes", label: "Notes", type: "textarea" },
            ],
        },
    ],
    meeting: [
        {
            key: "create_meeting", label: "Create Meeting", icon: "calendar",
            desc: "Schedule a meeting with agenda.",
            fields: [
                { name: "title", label: "Title", type: "text", required: true, half: true },
                { name: "date", label: "Date", type: "date", half: true },
                { name: "time", label: "Time", type: "text", half: true },
                { name: "location", label: "Location", type: "text", half: true },
                { name: "attendees", label: "Attendees", type: "textarea" },
                { name: "agenda", label: "Agenda", type: "textarea" },
            ],
        },
        {
            key: "meeting_notes", label: "Meeting Notes", icon: "note",
            desc: "Capture notes and decisions.",
            fields: [
                { name: "meeting_title", label: "Meeting Title", type: "text", required: true, half: true },
                { name: "date", label: "Date", type: "date", half: true },
                { name: "notes", label: "Notes", type: "textarea" },
                { name: "decisions", label: "Decisions", type: "textarea" },
            ],
        },
        {
            key: "action_item", label: "Action Item", icon: "check",
            desc: "Log a meeting action item.",
            fields: [
                { name: "task", label: "Task", type: "text", required: true, half: true },
                { name: "owner", label: "Owner", type: "text", half: true },
                { name: "due_date", label: "Due Date", type: "date", half: true },
                { name: "priority", label: "Priority", type: "select", options: PRIORITY, half: true },
                { name: "status", label: "Status", type: "select", options: ["Open", "In Progress", "Done"], half: true },
            ],
        },
        {
            key: "decision_log", label: "Decision Log", icon: "flag",
            desc: "Record an organizational decision.",
            fields: [
                { name: "decision", label: "Decision", type: "text", required: true, half: true },
                { name: "owner", label: "Owner", type: "text", half: true },
                { name: "date", label: "Date", type: "date", half: true },
                { name: "context", label: "Context", type: "textarea" },
            ],
        },
        {
            key: "followup", label: "Follow-up", icon: "refresh",
            desc: "Track a meeting follow-up.",
            fields: [
                { name: "related_meeting", label: "Related Meeting", type: "text", required: true, half: true },
                { name: "owner", label: "Owner", type: "text", half: true },
                { name: "due_date", label: "Due Date", type: "date", half: true },
                { name: "status", label: "Status", type: "select", options: ["Open", "In Progress", "Done"], half: true },
                { name: "task", label: "Task", type: "textarea" },
            ],
        },
    ],
    ceo: [
        {
            key: "strategic_note", label: "Strategic Note", icon: "note",
            desc: "Capture a strategic thought or directive.",
            fields: [
                { name: "title", label: "Title", type: "text", required: true, half: true },
                { name: "priority", label: "Priority", type: "select", options: PRIORITY, half: true },
                { name: "note", label: "Note", type: "textarea" },
            ],
        },
        {
            key: "ceo_recommendation", label: "CEO Recommendation", icon: "flag",
            desc: "Send a recommendation to a department.",
            fields: [
                { name: "topic", label: "Topic", type: "text", required: true, half: true },
                { name: "department", label: "Department", type: "text", half: true },
                { name: "recommendation", label: "Recommendation", type: "textarea" },
            ],
        },
    ],
};

/** Future AI modules shown as elegant, disabled "Coming Soon" cards. */
export const COMING_SOON = [
    { title: "AI Department Agent", body: "A dedicated agent analysing your submissions." },
    { title: "AI Insights", body: "Pattern detection across your department data." },
    { title: "Decision Cards", body: "Actionable cards generated for the CEO." },
    { title: "CEO Review", body: "See what the CEO decided on your updates." },
    { title: "Money Impact", body: "Financial impact of every submission." },
    { title: "Business Intelligence", body: "Cross-department intelligence for growth." },
];

export function formsForRole(role) {
    return FORMS[role] || [];
}
